/**
 * Main Game Class
 *
 * Orchestrates all game systems and manages game state
 */

import { GameLoop } from './GameLoop';
import { TimingEngine } from './TimingEngine';
import { AudioManager } from './AudioManager';
import { InputManager } from './InputManager';
import { ChartParser } from './ChartParser';
import { NoteManager } from './NoteManager';
import { SongManager } from './SongManager';
import { Renderer } from '../rendering/Renderer';
import { JudgmentSystem } from '../gameplay/JudgmentSystem';
import { ScoreManager } from '../gameplay/ScoreManager';
import { RecordingManager } from './RecordingManager';

export enum GameState {
  MENU,
  LOADING,
  GAMEPLAY,
  PAUSED,
  RESULTS,
  SETTINGS,
  RECORDING
}

export class Game {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;

  // Core systems
  private gameLoop: GameLoop;
  private timingEngine: TimingEngine;
  private audioManager: AudioManager;
  private inputManager: InputManager;
  private chartParser: ChartParser;
  private noteManager: NoteManager;
  private songManager: SongManager;
  private renderer: Renderer;
  private judgmentSystem: JudgmentSystem;
  private scoreManager: ScoreManager;
  private recordingManager: RecordingManager;

  // State
  private currentState: GameState = GameState.MENU;
  private currentSongId: string | null = null;
  private selectedSongIndex: number = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('Failed to get 2D context from canvas');
    }

    this.ctx = ctx;

    // Initialize systems
    this.audioManager = new AudioManager();
    this.timingEngine = new TimingEngine();

    // Share AudioContext between audio and timing
    const audioContext = this.audioManager.getAudioContext();
    if (audioContext) {
      // Inject the audioContext into timing engine
      (this.timingEngine as any).audioContext = audioContext;
    }

    this.inputManager = new InputManager();
    this.chartParser = new ChartParser();
    this.noteManager = new NoteManager();
    this.songManager = new SongManager();
    this.renderer = new Renderer(this.ctx);
    this.judgmentSystem = new JudgmentSystem();
    this.scoreManager = new ScoreManager();
    this.recordingManager = new RecordingManager();
    this.gameLoop = new GameLoop(this.update.bind(this), this.render.bind(this));

    // Set up input for menu
    this.setupMenuInput();

    console.log('Game systems initialized');
  }

  start(): void {
    console.log('Starting game...');
    this.gameLoop.start();
  }

  stop(): void {
    this.gameLoop.stop();
  }

  private setupMenuInput(): void {
    window.addEventListener('keydown', (event) => {
      // Debug logging
      if (event.code === 'Escape') {
        console.log(`ESC pressed! Current state: ${GameState[this.currentState]}`);
      }

      if (this.currentState === GameState.MENU) {
        if (event.code === 'Space') {
          event.preventDefault();
          this.startSong();
        }
        if (event.code === 'KeyR') {
          event.preventDefault();
          console.log('Starting recording mode...');
          this.startRecording();
        }
        if (event.code === 'ArrowUp' || event.code === 'KeyW') {
          event.preventDefault();
          this.selectPreviousSong();
        }
        if (event.code === 'ArrowDown' || event.code === 'KeyS') {
          event.preventDefault();
          this.selectNextSong();
        }
        if (event.code === 'Escape') {
          event.preventDefault();
          console.log('Opening settings...');
          this.openSettings();
        }
      }

      if (this.currentState === GameState.GAMEPLAY && event.code === 'Escape') {
        event.preventDefault();
        console.log('Attempting to pause...');
        this.pauseGame();
      }

      if (this.currentState === GameState.PAUSED && event.code === 'Escape') {
        event.preventDefault();
        console.log('Attempting to resume...');
        this.resumeGame();
      }

      if (this.currentState === GameState.RESULTS && event.code === 'Space') {
        event.preventDefault();
        this.returnToMenu();
      }

      if (this.currentState === GameState.SETTINGS && event.code === 'Escape') {
        event.preventDefault();
        console.log('Closing settings...');
        this.closeSettings();
      }

      if (this.currentState === GameState.RECORDING && event.code === 'Escape') {
        event.preventDefault();
        console.log('Stopping recording...');
        this.stopRecording();
      }
    });
  }

  private openSettings(): void {
    this.changeState(GameState.SETTINGS);
  }

  private closeSettings(): void {
    this.changeState(GameState.MENU);
  }

  private selectPreviousSong(): void {
    const songs = this.songManager.getAllSongs();
    this.selectedSongIndex = (this.selectedSongIndex - 1 + songs.length) % songs.length;
  }

  private selectNextSong(): void {
    const songs = this.songManager.getAllSongs();
    this.selectedSongIndex = (this.selectedSongIndex + 1) % songs.length;
  }

  private async startSong(): Promise<void> {
    this.changeState(GameState.LOADING);

    try {
      // Get the selected song
      const songs = this.songManager.getAllSongs();
      const song = songs[this.selectedSongIndex];
      if (!song) {
        console.error('No songs available');
        this.changeState(GameState.MENU);
        return;
      }

      this.currentSongId = song.id;
      console.log(`Loading song: ${song.title} by ${song.artist}`);

      // Load chart
      const chartData = await this.chartParser.loadChart(song.chartPath);
      this.noteManager.loadChart(chartData.notes);

      // Load audio
      await this.audioManager.loadAudio(song.id, song.audioPath);

      // Start gameplay
      this.startGameplay(song.id);
    } catch (error) {
      console.error('Failed to load song:', error);
      alert('Failed to load song. Make sure audio files are in the correct location.');
      this.changeState(GameState.MENU);
    }
  }

  private startGameplay(songId: string): void {
    // Reset systems
    this.scoreManager.reset();
    this.inputManager.clear();

    // Reset active notes but keep chart data
    this.noteManager.reset();

    // Start timing and audio
    this.timingEngine.start();
    this.audioManager.play(songId);

    this.changeState(GameState.GAMEPLAY);
    console.log('Gameplay started - chart has', this.noteManager.getTotalNotes(), 'notes');
  }

  private async startRecording(): Promise<void> {
    this.changeState(GameState.LOADING);

    try {
      // Get the selected song
      const songs = this.songManager.getAllSongs();
      const song = songs[this.selectedSongIndex];
      if (!song) {
        console.error('No songs available');
        this.changeState(GameState.MENU);
        return;
      }

      this.currentSongId = song.id;
      console.log(`🔴 Recording chart for: ${song.title} by ${song.artist}`);

      // Load audio (no chart needed for recording)
      await this.audioManager.loadAudio(song.id, song.audioPath);

      // Reset systems
      this.recordingManager.reset();
      this.inputManager.clear();

      // Start timing, audio, and recording
      this.timingEngine.start();
      this.audioManager.play(song.id);
      this.recordingManager.startRecording();

      this.changeState(GameState.RECORDING);
      console.log('🎤 Recording started! Press D/F/J/K to tap beats. Press ESC when done.');
    } catch (error) {
      console.error('Failed to start recording:', error);
      alert('Failed to load song for recording.');
      this.changeState(GameState.MENU);
    }
  }

  private stopRecording(): void {
    // Stop recording and get notes
    const recordedNotes = this.recordingManager.stopRecording();

    // Stop audio
    this.audioManager.stop();

    if (recordedNotes.length === 0) {
      alert('No notes recorded! Try again and tap along to the beat.');
      this.changeState(GameState.MENU);
      return;
    }

    // Get song metadata
    const songs = this.songManager.getAllSongs();
    const song = songs[this.selectedSongIndex];

    // Generate chart JSON
    const chartJson = this.recordingManager.generateChart({
      title: song.title,
      artist: song.artist,
      difficulty: song.difficulty || 'Normal',
      bpm: 160 // Default BPM - user can adjust manually
    });

    // Download the chart
    this.recordingManager.downloadChart(chartJson, song.id);

    alert(`✅ Chart recorded with ${recordedNotes.length} notes!\n\nThe file has been downloaded.\n\nTo use it:\n1. Find the downloaded ${song.id}-chart.json file\n2. Copy it to assets/songs/${song.id}/chart.json\n3. Refresh the game`);

    this.changeState(GameState.MENU);
  }

  private pauseGame(): void {
    this.timingEngine.pause();
    this.audioManager.pause();
    this.changeState(GameState.PAUSED);
    console.log('Game paused');
  }

  private resumeGame(): void {
    if (this.currentSongId) {
      this.timingEngine.resume();
      this.audioManager.resume(this.currentSongId);
      this.changeState(GameState.GAMEPLAY);
      console.log('Game resumed');
    }
  }

  private returnToMenu(): void {
    this.audioManager.stop();
    this.changeState(GameState.MENU);
  }

  private update(deltaTime: number): void {
    // Update systems based on current game state
    switch (this.currentState) {
      case GameState.MENU:
        // No updates needed
        break;
      case GameState.LOADING:
        // Loading handled asynchronously
        break;
      case GameState.GAMEPLAY:
        this.updateGameplay(deltaTime);
        break;
      case GameState.PAUSED:
        // No updates while paused
        break;
      case GameState.RESULTS:
        // No updates on results screen
        break;
      case GameState.SETTINGS:
        // No updates on settings screen
        break;
      case GameState.RECORDING:
        this.updateRecording(deltaTime);
        break;
    }
  }

  private updateGameplay(_deltaTime: number): void {
    const currentTime = this.timingEngine.getCurrentTime();

    // Update notes
    this.noteManager.update(currentTime);

    // Update input buffer
    this.inputManager.update();

    // Check for hits
    const recentInputs = this.inputManager.getRecentInputs();
    const activeNotes = this.noteManager.getActiveNotes();

    // Debug: Log active notes and inputs
    if (activeNotes.length > 0 && recentInputs.length > 0) {
      console.log(`Current time: ${currentTime.toFixed(0)}ms, Active notes: ${activeNotes.length}, Inputs: ${recentInputs.length}`);
    }

    for (const input of recentInputs) {
      if (input.consumed) continue;

      const judgment = this.judgmentSystem.checkHit(input, activeNotes, currentTime);
      if (judgment) {
        this.inputManager.consumeInput(input);
        this.scoreManager.addJudgment(judgment);
        this.renderer.showJudgment(judgment.type);
        console.log(`HIT! ${judgment.type}: ${judgment.timing.toFixed(1)}ms, Score: ${this.scoreManager.getScore()}`);
      }
    }

    // Check for auto-misses (notes that passed the judgment line)
    const missedJudgments = this.judgmentSystem.checkMisses(activeNotes, currentTime);
    for (const judgment of missedJudgments) {
      this.scoreManager.addJudgment(judgment);
      this.renderer.showJudgment('MISS');
      console.log(`AUTO-MISS! Note at ${judgment.note.time}ms passed judgment line. Combo reset!`);
    }

    // Check if song is complete
    if (this.noteManager.isComplete()) {
      this.endSong();
    }
  }

  private updateRecording(_deltaTime: number): void {
    const currentTime = this.timingEngine.getCurrentTime();

    // Update input buffer
    this.inputManager.update();

    // Capture inputs for recording
    const recentInputs = this.inputManager.getRecentInputs();

    for (const input of recentInputs) {
      if (input.consumed) continue;

      // Record the note
      this.recordingManager.recordNote(currentTime, input.lane);
      this.inputManager.consumeInput(input);

      // Show visual feedback
      this.renderer.showJudgment('PERFECT'); // Always show perfect for recording
    }
  }

  private endSong(): void {
    this.audioManager.stop();
    this.changeState(GameState.RESULTS);
    console.log('Song complete!');
    console.log(this.scoreManager.getStatistics());
  }

  private render(): void {
    // Clear screen
    this.ctx.fillStyle = '#0f0f1e';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    // Render based on current game state
    switch (this.currentState) {
      case GameState.MENU:
        this.renderMenu();
        break;
      case GameState.LOADING:
        this.renderLoading();
        break;
      case GameState.GAMEPLAY:
        this.renderGameplay();
        break;
      case GameState.PAUSED:
        this.renderPaused();
        break;
      case GameState.RESULTS:
        this.renderResults();
        break;
      case GameState.SETTINGS:
        this.renderSettings();
        break;
      case GameState.RECORDING:
        this.renderRecording();
        break;
    }
  }

  private renderSettings(): void {
    this.ctx.fillStyle = '#ffffff';
    this.ctx.font = 'bold 48px Arial';
    this.ctx.textAlign = 'center';
    this.ctx.fillText('Settings', this.canvas.width / 2, 100);

    const startY = 200;
    const lineHeight = 60;

    // Timing Windows
    this.ctx.font = 'bold 24px Arial';
    this.ctx.fillStyle = '#00ff88';
    this.ctx.fillText('Timing Windows', this.canvas.width / 2, startY);

    this.ctx.font = '20px Arial';
    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    this.ctx.fillText('Perfect: ±50ms', this.canvas.width / 2, startY + lineHeight);
    this.ctx.fillText('Great: ±100ms', this.canvas.width / 2, startY + lineHeight * 2);
    this.ctx.fillText('Good: ±150ms', this.canvas.width / 2, startY + lineHeight * 3);

    this.ctx.font = '18px Arial';
    this.ctx.fillStyle = 'rgba(255, 100, 100, 0.9)';
    this.ctx.fillText('Notes auto-MISS if 180ms past judgment line', this.canvas.width / 2, startY + lineHeight * 4);

    // Grading System
    this.ctx.font = 'bold 24px Arial';
    this.ctx.fillStyle = '#00ff88';
    this.ctx.fillText('Grading System', this.canvas.width / 2, startY + lineHeight * 5.2);

    this.ctx.font = '20px Arial';
    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    this.ctx.fillText('S+: 100% accuracy (All Perfect)', this.canvas.width / 2, startY + lineHeight * 6.2);
    this.ctx.fillText('S: ≥95% accuracy', this.canvas.width / 2, startY + lineHeight * 7.2);
    this.ctx.fillText('A: ≥85% accuracy', this.canvas.width / 2, startY + lineHeight * 8.2);
    this.ctx.fillText('B: ≥70% accuracy', this.canvas.width / 2, startY + lineHeight * 9.2);
    this.ctx.fillText('C: ≥50% accuracy', this.canvas.width / 2, startY + lineHeight * 10.2);

    // Controls help
    this.ctx.font = '18px Arial';
    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    this.ctx.fillText('ESC: Back to Menu', this.canvas.width / 2, this.canvas.height - 30);
  }

  private renderMenu(): void {
    this.ctx.fillStyle = '#ffffff';
    this.ctx.font = 'bold 48px Arial';
    this.ctx.textAlign = 'center';
    this.ctx.fillText('BeatShift', this.canvas.width / 2, 100);

    this.ctx.font = '18px Arial';
    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
    this.ctx.fillText('Select Song', this.canvas.width / 2, 160);

    // Show available songs
    const songs = this.songManager.getAllSongs();
    const startY = 220;

    songs.forEach((song, i) => {
      const y = startY + (i * 70);
      const isSelected = i === this.selectedSongIndex;

      // Selection box
      if (isSelected) {
        this.ctx.fillStyle = 'rgba(0, 255, 136, 0.2)';
        this.ctx.fillRect(100, y - 45, this.canvas.width - 200, 60);

        this.ctx.strokeStyle = '#00ff88';
        this.ctx.lineWidth = 2;
        this.ctx.strokeRect(100, y - 45, this.canvas.width - 200, 60);
      }

      // Song title
      this.ctx.font = 'bold 22px Arial';
      this.ctx.fillStyle = isSelected ? '#00ff88' : '#ffffff';
      this.ctx.textAlign = 'center';
      this.ctx.fillText(song.title, this.canvas.width / 2, y - 15);

      // Artist and difficulty
      this.ctx.font = '16px Arial';
      this.ctx.fillStyle = isSelected ? 'rgba(0, 255, 136, 0.8)' : 'rgba(255, 255, 255, 0.6)';
      this.ctx.fillText(`${song.artist} - ${song.difficulty}`, this.canvas.width / 2, y + 8);
    });

    // Controls
    this.ctx.font = '18px Arial';
    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    this.ctx.fillText('↑/↓ or W/S: Select Song  |  ESC: Settings', this.canvas.width / 2, this.canvas.height - 80);

    this.ctx.font = 'bold 20px Arial';
    this.ctx.fillStyle = '#00ff88';
    this.ctx.fillText('SPACE: Start  |  R: Record Chart', this.canvas.width / 2, this.canvas.height - 40);

    this.ctx.font = '16px Arial';
    this.ctx.fillStyle = 'rgba(255, 200, 100, 0.9)';
    this.ctx.fillText('(Recording mode: Tap along to the song to create a chart)', this.canvas.width / 2, this.canvas.height - 15);
  }

  private renderLoading(): void {
    this.ctx.fillStyle = '#ffffff';
    this.ctx.font = '32px Arial';
    this.ctx.textAlign = 'center';
    this.ctx.fillText('Loading...', this.canvas.width / 2, this.canvas.height / 2);
  }

  private renderGameplay(): void {
    // Render game with current state
    this.renderer.renderGameplay(
      this.noteManager.getActiveNotes(),
      this.scoreManager.getScore(),
      this.scoreManager.getCombo(),
      this.scoreManager.getAccuracy()
    );
  }

  private renderPaused(): void {
    this.renderGameplay();

    this.ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    this.ctx.fillStyle = '#ffffff';
    this.ctx.font = 'bold 48px Arial';
    this.ctx.textAlign = 'center';
    this.ctx.fillText('PAUSED', this.canvas.width / 2, this.canvas.height / 2 - 40);

    this.ctx.font = '24px Arial';
    this.ctx.fillStyle = '#00ff88';
    this.ctx.fillText('ESC: Resume', this.canvas.width / 2, this.canvas.height / 2 + 20);

    this.ctx.font = '20px Arial';
    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    this.ctx.fillText('(Song will continue from where you paused)', this.canvas.width / 2, this.canvas.height / 2 + 60);
  }

  private renderResults(): void {
    const stats = this.scoreManager.getStatistics();

    this.ctx.fillStyle = '#ffffff';
    this.ctx.font = 'bold 48px Arial';
    this.ctx.textAlign = 'center';
    this.ctx.fillText('Song Complete!', this.canvas.width / 2, 100);

    this.ctx.font = '32px Arial';
    this.ctx.fillStyle = '#00ff88';
    this.ctx.fillText(`Grade: ${this.scoreManager.getGrade()}`, this.canvas.width / 2, 160);

    this.ctx.font = '24px Arial';
    this.ctx.fillStyle = '#ffffff';
    const y = 220;
    this.ctx.fillText(`Score: ${stats.score.toLocaleString()}`, this.canvas.width / 2, y);
    this.ctx.fillText(`Accuracy: ${stats.accuracy.toFixed(2)}%`, this.canvas.width / 2, y + 40);
    this.ctx.fillText(`Max Combo: ${stats.maxCombo}`, this.canvas.width / 2, y + 80);

    this.ctx.font = '20px Arial';
    this.ctx.fillText(`Perfect: ${stats.judgments.perfect}`, this.canvas.width / 2, y + 140);
    this.ctx.fillText(`Great: ${stats.judgments.great}`, this.canvas.width / 2, y + 170);
    this.ctx.fillText(`Good: ${stats.judgments.good}`, this.canvas.width / 2, y + 200);
    this.ctx.fillText(`Miss: ${stats.judgments.miss}`, this.canvas.width / 2, y + 230);

    this.ctx.font = '18px Arial';
    this.ctx.fillStyle = '#00ff88';
    this.ctx.fillText('Press SPACE to return to menu', this.canvas.width / 2, y + 290);
  }

  private renderRecording(): void {
    // Draw lanes
    this.renderer.renderGameplay([], 0, 0, 0);

    // Recording overlay
    this.ctx.fillStyle = 'rgba(255, 0, 0, 0.1)';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    // Recording indicator
    this.ctx.fillStyle = '#ff0000';
    this.ctx.font = 'bold 48px Arial';
    this.ctx.textAlign = 'center';
    this.ctx.fillText('🔴 RECORDING', this.canvas.width / 2, 80);

    // Song info
    const songs = this.songManager.getAllSongs();
    const song = songs[this.selectedSongIndex];
    this.ctx.font = '24px Arial';
    this.ctx.fillStyle = '#ffffff';
    this.ctx.fillText(song.title, this.canvas.width / 2, 130);

    // Instructions
    this.ctx.font = 'bold 28px Arial';
    this.ctx.fillStyle = '#00ff88';
    this.ctx.fillText('Tap D / F / J / K to the beat!', this.canvas.width / 2, this.canvas.height / 2 - 50);

    // Note count
    const noteCount = this.recordingManager.getRecordedCount();
    this.ctx.font = '24px Arial';
    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    this.ctx.fillText(`Notes recorded: ${noteCount}`, this.canvas.width / 2, this.canvas.height / 2);

    // Time
    const currentTime = this.timingEngine.getCurrentTime();
    this.ctx.font = '20px Arial';
    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    this.ctx.fillText(`Time: ${(currentTime / 1000).toFixed(1)}s`, this.canvas.width / 2, this.canvas.height / 2 + 40);

    // Controls
    this.ctx.font = '18px Arial';
    this.ctx.fillStyle = '#ffaa00';
    this.ctx.fillText('ESC: Stop Recording & Download Chart', this.canvas.width / 2, this.canvas.height - 40);
  }

  private changeState(newState: GameState): void {
    this.currentState = newState;
    console.log(`State changed to: ${GameState[newState]}`);
  }
}
